import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { API_PATHS, API_QUERY_PARAMS } from '../constants/api-paths';
import { jsonHttpHeaders } from '../constants/http-headers';
import { Locataire } from '../models/locataire.model';

/**
 * Client HTTP des locataires ({@code /locataires}).
 */
@Injectable({
  providedIn: 'root',
})
export class LocataireService {
  constructor(private readonly http: HttpClient) {}

  /** Liste tous les locataires. */
  findAll(): Observable<Locataire[]> {
    return this.http.get<Locataire[]>(
      `${environment.apiUrl}${API_PATHS.locataires}`
    );
  }

  /** Recherche partielle sur nom / prénom ({@code ?q=}). */
  search(query: string): Observable<Locataire[]> {
    return this.http.get<Locataire[]>(
      `${environment.apiUrl}${API_PATHS.locataires}`,
      {
        params: new HttpParams().set(API_QUERY_PARAMS.q, query),
      }
    );
  }

  /** Locataires actifs rattachés à un logement ({@code ?logementId=}). */
  findByLogement(logementId: string): Observable<Locataire[]> {
    return this.http.get<Locataire[]>(
      `${environment.apiUrl}${API_PATHS.locataires}`,
      {
        params: new HttpParams().set(API_QUERY_PARAMS.logementId, logementId),
      }
    );
  }

  /** Détail d'un locataire. */
  findById(id: string): Observable<Locataire> {
    return this.http.get<Locataire>(
      `${environment.apiUrl}${API_PATHS.locataire(id)}`
    );
  }

  /** Crée un locataire. */
  create(locataire: Locataire): Observable<Locataire> {
    return this.http.post<Locataire>(
      `${environment.apiUrl}${API_PATHS.locataires}`,
      locataire,
      { headers: jsonHttpHeaders() }
    );
  }

  /** Met à jour un locataire existant. */
  update(locataire: Locataire): Observable<Locataire> {
    return this.http.put<Locataire>(
      `${environment.apiUrl}${API_PATHS.locataire(locataire.idLocataireDto ?? '')}`,
      locataire,
      { headers: jsonHttpHeaders() }
    );
  }

  /** Désactive un locataire (soft delete). */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}${API_PATHS.locataire(id)}`
    );
  }

  /**
   * Suppression physique — bail non signé et aucun loyer versé
   * ({@code ?definitive}).
   */
  deleteDefinitive(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}${API_PATHS.locataire(id)}`,
      {
        params: new HttpParams().set(API_QUERY_PARAMS.definitive, 'true'),
      }
    );
  }

  /** Réactive un locataire désactivé ({@code POST .../reactiver}). */
  reactiver(id: string): Observable<Locataire> {
    return this.http.post<Locataire>(
      `${environment.apiUrl}${API_PATHS.locataireReactiver(id)}`,
      {}
    );
  }
}
